'use client';
import { useOptimistic, useTransition } from 'react';

import Container from '@/components/@base/container';
import Group from '@/components/@base/input/@shared/group';
import Label from '@/components/@base/input/@shared/label';
import Switch from '@/components/@base/input/switch';
import Tooltip from '@/components/@base/tooltip';
import XImage from '@/components/@base/x-image';
import Props from '@/components/app/archive/components/filter/@shared/items/view-selector/type';
import { FILTER_KEYS } from '@/components/app/archive/helpers';
import { useFilterParams } from '@/components/app/archive/helpers/hooks';
import IconTextCombo from '@/components/shared/components/icon-text-combo';

import { DATA_CY_VIEW_SELECTOR } from '@/constant/cypress';
import imagesList from '@/constant/images-list';
import { pageLevelLocalization } from '@/constant/localization';

const { archive: archiveLocalization } = pageLevelLocalization;

export default function ViewSelector({ initialFilters }: Props) {
  const { updateParams } = useFilterParams({ searchParams: initialFilters });

  const [isSubscriptionFilter, setIsSubscriptionFilter] = useOptimistic(
    initialFilters[FILTER_KEYS.IS_SUBSCRIPTION] || false,
  );

  const [demoFilter, setDemoFilter] = useOptimistic(
    initialFilters[FILTER_KEYS.VIEW_DEMO] || false,
  );
  const [sellableFilter, setSellableFilter] = useOptimistic(
    initialFilters.hasOwnProperty(FILTER_KEYS.SELLABLE)
      ? initialFilters[FILTER_KEYS.SELLABLE]
      : true,
  );
  let [isPending, startTransition] = useTransition();

  const handleFilterChange = (key: string, value: boolean, forceSetFalse: boolean = false) => {
    const actions: Record<string, (val: boolean) => void> = {
      [FILTER_KEYS.IS_SUBSCRIPTION]: setIsSubscriptionFilter,
      [FILTER_KEYS.VIEW_DEMO]: setDemoFilter,
      [FILTER_KEYS.SELLABLE]: setSellableFilter,
    };
    startTransition(() => {
      actions[key]?.(value);
      updateParams(key, value, forceSetFalse);
    });
  };

  return (
    <>
      <Group
        className='flex items-center justify-between'
        data-pending={isPending ? '' : undefined}
      >
        <Label className='ml-4'>
          <IconTextCombo
            wrapperClassName='my-2 pb-0'
            Icon={
              <XImage
                src={imagesList.icons.unavailableProducts}
                alt={archiveLocalization.filter.isSubscription}
              />
            }
            title={archiveLocalization.filter.isSubscription}
            textClassName='text-base font-bold'
          />
        </Label>
        <Switch
          data-cy={DATA_CY_VIEW_SELECTOR}
          checked={isSubscriptionFilter}
          onChange={(checked) => handleFilterChange(FILTER_KEYS.IS_SUBSCRIPTION, checked)}
        />
      </Group>

      <Group
        className='flex items-center justify-between'
        data-pending={isPending ? '' : undefined}
      >
        <Label className='ml-4'>
          <Container center className='gap-2'>
            <IconTextCombo
              wrapperClassName='my-2 pb-0'
              Icon={
                <XImage
                  src={imagesList.icons.showLive}
                  alt={archiveLocalization.filter.live}
                />
              }
              title={archiveLocalization.filter.live}
              textClassName='text-base font-bold'
            />
            <Container className='z-10'>
              <XImage
                src={imagesList.icons.infoIcon}
                alt={archiveLocalization.filter.live}
                width={16}
                height={16}
                data-tooltip-id={archiveLocalization.filter.live}
                data-tooltip-content={archiveLocalization.filter.demoTooltip}
              />
              <Tooltip id={archiveLocalization.filter.live} />
            </Container>
          </Container>
        </Label>
        <Switch
          data-cy={DATA_CY_VIEW_SELECTOR}
          checked={demoFilter}
          onChange={(checked) => handleFilterChange(FILTER_KEYS.VIEW_DEMO, checked)}
        />
      </Group>

      <Group
        className='flex items-center justify-between'
        data-pending={isPending ? '' : undefined}
      >
        <Label className='ml-4'>
          <IconTextCombo
            wrapperClassName='my-2 pb-0'
            Icon={
              <XImage
                src={imagesList.icons.unavailableProducts}
                alt={archiveLocalization.filter.unavailableProducts}
              />
            }
            title={archiveLocalization.filter.unavailableProducts}
            textClassName='text-base font-bold'
          />
        </Label>
        <Switch
          data-cy={DATA_CY_VIEW_SELECTOR}
          checked={sellableFilter === false}
          onChange={(checked) => handleFilterChange(FILTER_KEYS.SELLABLE, !checked, true)}
        />
      </Group>
    </>
  );
}
